import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
  Cloud, Server, Shield, Database, Network, Settings,
  CheckCircle, Clock, AlertTriangle, MessageSquare, 
  ArrowRight, Download, FileText, Play, Book, Users,
  Lock, Globe, Key, Target, Brain, ChevronRight,
  Award, Sparkles, ArrowLeft
} from 'lucide-react';
import ChatBot from '../../components/ChatBot';

const translations = {
  es: {
    title: 'AZ-104T00-A: Administrador de Microsoft Azure',
    description: 'Aprende a administrar suscripciones de Azure, asegurar identidades, administrar infraestructura, configurar redes virtuales y más.',
    modules: {
      prerequisites: {
        title: 'Prerrequisitos para administradores de Azure',
        duration: '4 días',
        description: 'Aprende los conceptos fundamentales necesarios para convertirte en un administrador de Azure.'
      },
      identities: {
        title: 'Gestionar identidades y gobernanza en Azure',
        duration: '3 días',
        description: 'Aprende a administrar suscripciones de Azure y recursos, implementar y administrar el almacenamiento.'
      },
      storage: {
        title: 'Implementar y gestionar almacenamiento',
        duration: '3 días',
        description: 'Aprende a implementar y administrar el almacenamiento en Azure.'
      },
      compute: {
        title: 'Implementar y gestionar recursos de computación',
        duration: '4 días',
        description: 'Aprende a implementar y administrar máquinas virtuales y otros recursos de computación.'
      }
    },
    actions: {
      start: 'Comenzar módulo',
      continue: 'Continuar',
      askMentor: 'Preguntar al mentor'
    }
  },
  en: {
    title: 'AZ-104T00-A: Microsoft Azure Administrator',
    description: 'Learn how to manage Azure subscriptions, secure identities, administer infrastructure, configure virtual networking, and more.',
    modules: {
      prerequisites: {
        title: 'Prerequisites for Azure administrators',
        duration: '4 days',
        description: 'Learn the foundational concepts needed to become an Azure administrator.'
      },
      identities: {
        title: 'Manage identities and governance in Azure',
        duration: '3 days',
        description: 'Learn to manage Azure subscriptions and resources, implement and manage storage.'
      },
      storage: {
        title: 'Implement and manage storage',
        duration: '3 days',
        description: 'Learn to implement and manage storage in Azure.'
      },
      compute: {
        title: 'Implement and manage compute resources',
        duration: '4 days',
        description: 'Learn to implement and manage virtual machines and other compute resources.'
      }
    },
    actions: {
      start: 'Start module',
      continue: 'Continue',
      askMentor: 'Ask mentor'
    }
  }
};

const AzureAdministrator = () => {
  const [showChatbot, setShowChatbot] = useState(false);
  const [completedModules, setCompletedModules] = useState<string[]>([]);
  const { language } = useLanguage();
  const t = translations[language];
  const navigate = useNavigate();

  // Load completed modules from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('completedModules-az104');
    if (saved) {
      setCompletedModules(JSON.parse(saved));
    }
  }, []);

  // Save completed modules to localStorage
  useEffect(() => {
    localStorage.setItem('completedModules-az104', JSON.stringify(completedModules));
  }, [completedModules]);

  const modules = [
    {
      id: 'prerequisites',
      title: t.modules.prerequisites.title,
      description: t.modules.prerequisites.description,
      duration: t.modules.prerequisites.duration,
      icon: Book,
      path: '/training/azure-admin-module-1'
    },
    {
      id: 'identities',
      title: t.modules.identities.title,
      description: t.modules.identities.description,
      duration: t.modules.identities.duration,
      icon: Key
    },
    {
      id: 'storage',
      title: t.modules.storage.title,
      description: t.modules.storage.description,
      duration: t.modules.storage.duration,
      icon: Database
    },
    {
      id: 'compute',
      title: t.modules.compute.title,
      description: t.modules.compute.description,
      duration: t.modules.compute.duration,
      icon: Server
    }
  ];

  const isModuleAvailable = (index: number) => {
    if (index === 0) return true;
    return completedModules.includes(modules[index - 1].id);
  };

  const getModuleProgress = (moduleId: string) => {
    if (completedModules.includes(moduleId)) return 100;
    
    // If it's the current module in progress
    if (moduleId === 'prerequisites') {
      const progressStr = localStorage.getItem('completedSections-az104-module1');
      if (progressStr) {
        const completedSections = JSON.parse(progressStr);
        return Math.round((completedSections.length / 3) * 100); // 3 is the total number of sections
      }
    }
    
    return 0;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Course Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg p-6 text-white shadow-lg mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">{t.title}</h1>
              <p className="mt-4 text-white/90 max-w-3xl">{t.description}</p>
            </div>
            <button
              onClick={() => setShowChatbot(true)}
              className="bg-white text-purple-600 px-4 py-2 rounded-lg hover:bg-white/90 transition-colors flex items-center shadow-md hover:shadow-lg"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              {t.actions.askMentor}
            </button>
          </div>
        </div>

        {/* Modules Grid */}
        <div className="grid grid-cols-1 gap-6">
          {modules.map((module, index) => {
            const isAvailable = isModuleAvailable(index);
            const progress = getModuleProgress(module.id);
            const isCompleted = completedModules.includes(module.id);

            return (
              <div
                key={module.id}
                className="dark-card p-6 hover:border-accent/50 transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center">
                      <module.icon className="h-5 w-5 text-accent" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-foreground">{module.title}</h3>
                      <p className="text-foreground/70">{module.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 text-foreground/50 mr-2" />
                    <span className="text-sm text-foreground/70">{module.duration}</span>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-foreground/70">Progress</span>
                    <span className="text-sm font-medium text-foreground">{progress}%</span>
                  </div>
                  <div className="h-2 bg-secondary rounded-full">
                    <div
                      className="h-2 bg-accent rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                </div>

                <div className="mt-4 flex justify-end">
                  {!isAvailable ? (
                    <div className="flex items-center text-foreground/50">
                      <Lock className="h-5 w-5 mr-2" />
                      <span>Complete previous module first</span>
                    </div>
                  ) : isCompleted ? (
                    <button
                      onClick={() => module.path && navigate(module.path)}
                      className="inline-flex items-center text-accent hover:text-accent/90"
                    >
                      Review
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </button>
                  ) : (
                    <button
                      onClick={() => module.path && navigate(module.path)}
                      className="inline-flex items-center text-accent hover:text-accent/90"
                    >
                      {progress > 0 ? t.actions.continue : t.actions.start}
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Chatbot */}
      {showChatbot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-2xl">
            <ChatBot 
              role="azure-admin"
              onClose={() => setShowChatbot(false)} 
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AzureAdministrator;